import React, { useState, useMemo, useEffect, useRef } from "react";
import { httpClient } from '../../../HttpClient';
import "../Suivi.css";
import { IoIosSearch } from "react-icons/io";
import { CiFilter } from "react-icons/ci";
import { X } from "lucide-react";
import FicheClientOverlay from "../../Portefeuille/FicheClientOverlay";
import { missionCommencee, missionActive, estBloque, classeLigneBloquee } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";

function SuiviIS({ suivi, loading, refetch }) {
  const [selectedYear, setSelectedYear] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRows, setFilterRows] = useState([
    { column: "collaborateur", selectedValues: [] },
  ]);
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const overlayRef = useRef(null);
  const [currentSuivi, setCurrentSuivi] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  // Fiche client partagée (mêmes états que SuiviJuridiqueAGO / SuiviBilan)
  const [ficheClientOpen, setFicheClientOpen] = useState(false);
  const [ficheClientData, setFicheClientData] = useState(null);
  const [ficheClientLoading, setFicheClientLoading] = useState(false);
  const ficheClientCache = useRef({});

  const showClientDetails = async (client) => {
    setFicheClientOpen(true);
    if (ficheClientCache.current[client.id]) {
      setFicheClientData(ficheClientCache.current[client.id]);
      setFicheClientLoading(false);
      return;
    }
    setFicheClientData(client);
    setFicheClientLoading(true);
    try {
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/clients/${client.id}`
      );
      ficheClientCache.current[client.id] = data;
      setFicheClientData(data);
    } catch (err) {
      console.error("Erreur chargement client:", err);
    } finally {
      setFicheClientLoading(false);
    }
  };

  // ✅ Fermer overlay au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        setShowFilterOverlay(false);
      }
    };
    if (showFilterOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterOverlay]);

  // ✅ Années disponibles à partir des suivis
  const getAvailableYears = (clients) => {
    if (!clients || clients.length === 0) return [];
    let years = [];

    clients.forEach((client) => {
      const clotureYear = new Date(client.dateCloture).getFullYear();
      if (!isNaN(clotureYear)) years.push(clotureYear);
      client.suiviIS?.forEach((entry) => {
        if (entry.annee) years.push(entry.annee);
      });
    });

    years = [...new Set(years)].sort((a, b) => a - b);
    const maxYear = Math.max(...years);
    years.push(maxYear + 1);
    return years;
  };

  const availableYears = getAvailableYears(suivi);

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      if (availableYears.includes(2025)) {
        setSelectedYear(2025);
      } else {
        setSelectedYear(Math.max(...availableYears));
      }
    }
  }, [availableYears, selectedYear]);

  // ✅ Valeurs uniques pour filtres
  const uniqueValues = useMemo(() => {
    const values = {
      collaborateur: Array.from(new Set(suivi.map((c) => c.collaborateur))),
      expertComptable: Array.from(new Set(suivi.map((c) => c.expertComptable))),
      // typeIS: Array.from(
      //   new Set(
      //     suivi.flatMap((c) => c.suiviIS?.map((e) => e.typeIS)).filter(Boolean)
      //   )
      // ),
    };
    return values;
  }, [suivi]);

  // ✅ Gestion filtres dynamiques
  const handleAddFilter = () => {
    setFilterRows((prev) => [
      ...prev,
      { column: "collaborateur", selectedValues: [] },
    ]);
  };

  const handleRemoveFilter = (indexToRemove) => {
    setFilterRows((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleFilterChange = (index, field, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) =>
        i === index ? { ...f, [field]: value, selectedValues: [] } : f
      )
    );
  };

  const handleCheckboxValueChange = (index, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const selectedValues = f.selectedValues || [];
        const updated = selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];
        return { ...f, selectedValues: updated };
      })
    );
  };

  const handleResetFilters = () => {
    setFilterRows([{ column: "collaborateur", selectedValues: [] }]);
    setSearchTerm("");
  };

  // ✅ Filtrage global
  const filteredData = useMemo(() => {
    return suivi.filter((client) => {
      const matchesSearch = client.nom
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesFilters = filterRows.every((filter) => {
        if (!filter.selectedValues || filter.selectedValues.length === 0)
          return true;
        if (filter.column === "typeIS") {
          return client.suiviIS?.some((t) =>
            filter.selectedValues.includes(t.typeIS)
          );
        }
        return filter.selectedValues.includes(client[filter.column]);
      });

      const clotureYear = new Date(client.dateCloture).getFullYear();
      const matchesYear =
        !selectedYear ||
        selectedYear >= clotureYear ||
        client.suiviIS?.some((t) => t.annee === selectedYear);

      // Mission pas encore débutée cette année-là → masqué ; client archivé
      // depuis une année antérieure → masqué aussi.
      const missionDebutee =
        !selectedYear ||
        (missionCommencee(client, selectedYear) &&
          missionActive(client, selectedYear));

      return matchesSearch && matchesFilters && matchesYear && missionDebutee;
    });
  }, [suivi, searchTerm, filterRows, selectedYear]);

  // ✅ Actions IS

  const handleEditClick = (entry, client, section) => {
    setCurrentSuivi(entry);
    setShowModal(true);
    setSelectedClient(client); // 🔹 Nouveau
    setActiveSection(section);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSuivi((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    setLoadingUpdate(true);
    setShowModal(false);
    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/suiviIS/${selectedYear}/${selectedClient.id}`,
        currentSuivi
      );
    } catch (err) {
      console.error("Erreur lors de la mise à jour du suiviIS:", err);
    } finally {
      setLoadingUpdate(false);
      if (refetch) refetch();
    }
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
          <div className="h-9 w-64 rounded-md bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-5">
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#6e1a36]">
                {["Dossier", "IS N-1", "IS N-2", "Acompte 1", "Acompte 2", "Acompte 3", "Acompte 4"].map((col, i) => (
                  <th key={i} className="px-3 py-3.5 text-left">
                    <div className="h-3 rounded bg-[#ffffff33] animate-pulse" style={{ width: "70%" }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {["40%", "15%", "15%", "15%", "15%", "15%", "15%"].map((w, j) => (
                    <td key={j} className="px-3 py-3">
                      <div className="h-3 rounded bg-slate-200 animate-pulse" style={{ width: w }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const getRowColor = (statut) => {
    if (!statut || statut === "" || statut === "Saisi") return "transparent";
    if (statut === "Demander l'accord de paiement") return "#FFD580"; // Mauve/orangé
    if (statut === "Statut paiement") return "#A6F0A6"; // Vert clair
    return "transparent";
  };

  return (
    <div>
      {/* Barre de recherche + année + filtre */}
      <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filtrer par nom du dossier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 w-full rounded-md bg-slate-50 border border-slate-200 text-sm focus:bg-white focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-5">
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-slate-300 rounded-md
                                    h-9 px-2 text-xs leading-tight w-20
                                    bg-white"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowFilterOverlay(!showFilterOverlay)}
            className={`flex items-center gap-2 h-9 px-3 rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
          >
            <CiFilter className="w-3 h-3" />
            Filtrer
          </button>
        </div>
        {showFilterOverlay && (
          <div
            ref={overlayRef}
            className="fixed top-[40%] left-[65%] -translate-x-1/2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg z-50 w-2/5 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between mb-2">
              <p className="font-semibold">Filtres avancés</p>
              <button
                className="text-sm text-slate-600 hover:text-slate-800"
                onClick={handleResetFilters}
              >
                Réinitialiser
              </button>
            </div>
            {filterRows.map((filter, index) => (
              <div key={index} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <select
                    value={filter.column}
                    onChange={(e) =>
                      handleFilterChange(index, "column", e.target.value)
                    }
                    className="border border-slate-200 rounded-md p-1 text-sm"
                  >
                    {Object.keys(uniqueValues).map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveFilter(index)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    - Supprimer
                  </button>
                </div>
                <div className="flex flex-col ml-2">
                  {uniqueValues[filter.column]?.map((value) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={filter.selectedValues.includes(value)}
                        onChange={() => handleCheckboxValueChange(index, value)}
                        className="mr-2"
                      />
                      {value || "(vide)"}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button
              className="text-sm text-slate-700 hover:text-slate-900 mt-2"
              onClick={handleAddFilter}
            >
              + Ajouter filtre
            </button>
          </div>
        )}
      </div>

      {/* Tableau principal */}
      <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 250px)", minHeight: "420px" }}
        >
          <table className="w-full" style={{ fontSize: "0.72rem" }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#6e1a36] text-xs">
                <th className="px-2 py-3.5 text-white font-semibold text-left">
                  Dossier
                </th>
                {[
                  "IS N-1",
                  "IS N-2",
                  "Acompte 1",
                  "Acompte 2",
                  "Acompte 3",
                  "Acompte 4",
                ].map((col) => (
                  <th
                    key={col}
                    className="px-2 py-3.5 text-white font-semibold text-center"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-12 text-sm text-slate-500"
                  >
                    Aucun client trouvé
                  </td>
                </tr>
              )}

              {filteredData.map((client) => {
                const rows =
                  Array.isArray(client.suiviIS) && client.suiviIS.length > 0
                    ? client.suiviIS
                    : [{}];

                return rows.map((entry, i) => (
                  <tr
                    key={`${client.id}-${i}`}
                    className={`cursor-pointer hover:bg-slate-50/50 transition-colors ${classeLigneBloquee(client)}`}
                  >
                    <td
                      style={{ width: "20%" }}
                      onClick={() => i === 0 && showClientDetails(client)}
                      className="px-2 py-2 font-medium text-slate-900 cursor-pointer hover:text-[#9B1B5D] transition-colors"
                    >
                      {i === 0 ? client.nom : ""}
                      {i === 0 && estBloque(client) && <BadgeBloque />}
                    </td>
                    <td
                      className="px-2 py-1.5 text-center text-slate-700"
                      onClick={() =>
                        handleEditClick(entry, client, "isTheorique")
                      }
                    >
                      {entry.isTheorique || ""}
                    </td>
                    <td
                      className="px-2 py-1.5 text-center text-slate-700"
                      onClick={() =>
                        handleEditClick(entry, client, "isTheorique2")
                      }
                    >
                      {entry.isTheorique2 || ""}
                    </td>

                    {(() => {
                      let baseIS;
                      if (entry.isTheorique) {
                        baseIS = entry.isTheorique;
                      } else if (!entry.isTheorique && entry.isTheorique2) {
                        baseIS = entry.isTheorique2;
                      }

                      let A1 = entry.montantAcompte1;
                      let A2 = entry.montantAcompte2;
                      let A3 = entry.montantAcompte3;
                      let A4 = entry.montantAcompte4;

                      if (baseIS && !A1 && !A2 && !A3 && !A4) {
                        if (entry.isTheorique) {
                          // Calcul progressif basé sur isTheorique
                          A1 = (baseIS * 0.25).toFixed(2);
                          A2 = (baseIS * 0.5 - A1).toFixed(2);
                          A3 = (baseIS * 0.75 - A1 - A2).toFixed(2);
                          A4 = (baseIS - A1 - A2 - A3).toFixed(2);
                        } else {
                          // Calcul simple 25% chacun basé sur isTheorique2
                          A1 = (baseIS * 0.25).toFixed(2);
                          A2 = (baseIS * 0.25).toFixed(2);
                          A3 = (baseIS * 0.25).toFixed(2);
                          A4 = (baseIS * 0.25).toFixed(2);
                        }
                      }

                      return (
                        <>
                          <td
                            className="px-2 py-1.5 text-center text-slate-700"
                            style={{
                              backgroundColor: getRowColor(
                                entry.statutDeclaration1
                              ),
                            }}
                            onClick={() =>
                              handleEditClick(entry, client, "ac1")
                            }
                          >
                            {A1 || ""}
                          </td>
                          <td
                            className="px-2 py-1.5 text-center text-slate-700"
                            style={{
                              backgroundColor: getRowColor(
                                entry.statutDeclaration2
                              ),
                            }}
                            onClick={() =>
                              handleEditClick(entry, client, "ac2")
                            }
                          >
                            {A2 || ""}
                          </td>
                          <td
                            className="px-2 py-1.5 text-center text-slate-700"
                            style={{
                              backgroundColor: getRowColor(
                                entry.statutDeclaration3
                              ),
                            }}
                            onClick={() =>
                              handleEditClick(entry, client, "ac3")
                            }
                          >
                            {A3 || ""}
                          </td>
                          <td
                            className="px-2 py-1.5 text-center text-slate-700"
                            style={{
                              backgroundColor: getRowColor(
                                entry.statutDeclaration4
                              ),
                            }}
                            onClick={() =>
                              handleEditClick(entry, client, "ac4")
                            }
                          >
                            {A4 || ""}
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal édition */}
      {showModal && (
        <div className="overlayAC">
          <div className="inoverlayLead">
            <button
              aria-label="Fermer"
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              style={{ zIndex: 10000 }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="w-[85%] mx-auto mt-5">
              <div className="border-b-4 border-[#8B1538] pb-4 mb-6">
                <h2 className="text-xl font-bold text-slate-900 m-0">
                  Suivi IS : {selectedClient.nom || "Client inconnu"}
                </h2>
                <p className="text-xs text-slate-500 mt-1 m-0">
                  Année {selectedYear}
                </p>
              </div>

              {activeSection === "isTheorique" && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    IS N-1
                  </label>
                  <input
                    className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    name="isTheorique"
                    type="number"
                    value={currentSuivi.isTheorique || ""}
                    onChange={handleInputChange}
                  />
                </div>
              )}
              {activeSection === "isTheorique2" && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    IS N-2
                  </label>
                  <input
                    className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    name="isTheorique2"
                    type="number"
                    value={currentSuivi.isTheorique2 || ""}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              {[1, 2, 3, 4].map(
                (n) =>
                  activeSection === `ac${n}` && (
                    <div
                      className="flex flex-col"
                      key={`edit-acompte-block-${n}`}
                    >
                      <h4 className="text-sm font-bold text-[#9B1B5D] m-0 mb-4">
                        Acompte {n} — {selectedYear}
                      </h4>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Montant Acompte {n}
                        </label>
                        <input
                          name={`montantAcompte${n}`}
                          type="number"
                          value={currentSuivi[`montantAcompte${n}`] || ""}
                          placeholder={(() => {
                            const baseIS = currentSuivi.isTheorique
                              ? currentSuivi.isTheorique
                              : !currentSuivi.isTheorique &&
                                currentSuivi.isTheorique2
                              ? currentSuivi.isTheorique2
                              : null;

                            if (!baseIS) return "";

                            // Si c'est isTheorique, calcul progressif
                            if (currentSuivi.isTheorique) {
                              const A1 = baseIS * 0.25;
                              const A2 = baseIS * 0.5 - A1;
                              const A3 = baseIS * 0.75 - A1 - A2;
                              const A4 = baseIS - A1 - A2 - A3;
                              const acomptes = [A1, A2, A3, A4];
                              return acomptes[n - 1].toFixed(2);
                            } else {
                              // Sinon isTheorique2, 25% chacun
                              return (baseIS * 0.25).toFixed(2);
                            }
                          })()}
                          onChange={handleInputChange}
                          className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Statut Déclaration {n}
                        </label>
                        <select
                          name={`statutDeclaration${n}`}
                          value={currentSuivi[`statutDeclaration${n}`] || ""}
                          onChange={handleInputChange}
                          className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                        >
                          <option value="Saisi">Saisi</option>
                          <option value="Demander l'accord de paiement">
                            Demander l'accord de paiement
                          </option>
                          <option value="Statut paiement">
                            Statut paiement
                          </option>
                        </select>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Commentaire
                        </label>
                        <textarea
                          className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                          name={`commentaire${n}`}
                          value={currentSuivi[`commentaire${n}`] || ""}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </div>
                    </div>
                  )
              )}

              <div className="flex justify-end gap-3 mt-6 mb-10">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 h-10 rounded-md font-medium text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpdate}
                  className="px-5 h-10 rounded-md font-semibold text-sm bg-[#840040] hover:bg-[#6e1a36] text-white"
                >
                  {loadingUpdate ? "En cours..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FicheClientOverlay
        client={ficheClientData}
        open={ficheClientOpen}
        loading={ficheClientLoading}
        initialTab="general"
        onClose={() => setFicheClientOpen(false)}
        onClientUpdated={(updated) => {
          ficheClientCache.current[updated.id] = updated;
          setFicheClientData(updated);
        }}
      />
    </div>
  );
}

export default SuiviIS;
